import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { TextField, Button, MenuItem, IconButton, CircularProgress, Box, Paper, List, ListItem, ListItemText, ClickAwayListener, Typography, Stack, Divider } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import API_BASE_URL from '../config';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { styled } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';
// Moved outside component — recreating this 36-key object on every render was wasteful
const STATE_ABBREVIATIONS = {
    "Andaman and Nicobar Islands": "AN", "Andhra Pradesh": "AP", "Arunachal Pradesh": "AR",
    "Assam": "AS", "Bihar": "BR", "Chandigarh": "CH", "Chhattisgarh": "CG",
    "Dadra and Nagar Haveli and Daman and Diu": "DN", "Delhi": "DL", "Goa": "GA",
    "Gujarat": "GJ", "Haryana": "HR", "Himachal Pradesh": "HP", "Jammu and Kashmir": "JK",
    "Jharkhand": "JH", "Karnataka": "KA", "Kerala": "KL", "Ladakh": "LA",
    "Lakshadweep": "LD", "Madhya Pradesh": "MP", "Maharashtra": "MH", "Manipur": "MN",
    "Meghalaya": "ML", "Mizoram": "MZ", "Nagaland": "NL", "Odisha": "OD",
    "Puducherry": "PY", "Punjab": "PB", "Rajasthan": "RJ", "Sikkim": "SK",
    "Tamil Nadu": "TN", "Telangana": "TS", "Tripura": "TR", "Uttar Pradesh": "UP",
    "Uttarakhand": "UK", "West Bengal": "WB"
};

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: `0 12px 40px 0 ${alpha(theme.palette.common.black, 0.12)}`,
    },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(1.5),
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
        },
        '&.Mui-focused': {
            backgroundColor: alpha(theme.palette.primary.main, 0.03),
            '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
                borderWidth: '2px',
            },
        },
    },
    '& .MuiInputLabel-root': {
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    color: 'white',
    transition: 'all 0.3s ease',
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 5px 15px ${alpha(theme.palette.primary.main, 0.3)}`,
    },
}));

const OutlinedActionButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    '&:hover': {
        transform: 'translateY(-2px)',
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
        borderColor: theme.palette.primary.main,
    },
}));

const MemberCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    background: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
    backdropFilter: 'blur(10px)',
    boxShadow: `0 8px 32px 0 ${alpha(theme.palette.common.black, 0.08)}`,
    transition: 'all 0.3s ease',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
    '&:hover': {
        boxShadow: `0 12px 40px 0 ${alpha(theme.palette.common.black, 0.12)}`,
    },
}));
const InfoTypeIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    marginRight: theme.spacing(2),
  }));
const SearchResultsPaper = styled(Paper)(({ theme }) => ({
    position: 'absolute',
    width: '100%',
    maxHeight: 200,
    overflow: 'auto',
    marginTop: theme.spacing(0.5),
    zIndex: 1000,
    boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.15)}`,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    top: '100%',
    left: 0,
}));

const SearchResultItem = styled(ListItem)(({ theme }) => ({
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
    },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.error.main,
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.1),
        transform: 'scale(1.1)',
    },
}));

const AddFamilyMember = React.forwardRef(({ formData, editMode, setAllFamilyMembers, villageName, pinCode, index }, ref) => {
    // Self row (index 0) — always shown, never sent to save API
    const [selfRow, setSelfRow] = useState(null);
    // Existing members fetched from API — shown but NOT re-sent on save
    const [existingMembers, setExistingMembers] = useState([]);
    // New members added via button — ONLY these are sent to save API
    const [newMembers, setNewMembers] = useState([]);

    const [fetchedData, setFetchedData] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    const fetchUsers = useCallback(async (query) => {
        if (!query) { setSearchResults([]); return; }
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/search`, { params: { first_name: query } });
            setSearchResults(response.data);
        } catch (error) {
          //  console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const debouncedFetchUsers = useCallback(
        (() => {
            let t;
            return (q) => { clearTimeout(t); t = setTimeout(() => fetchUsers(q), 500); };
        })(),
        [fetchUsers]
    );

    useEffect(() => {
        if (!villageName) return;
        axios.get(`${API_BASE_URL}/fetch-village-details`, {
            params: { village_name: villageName },
            withCredentials: true
        })
        .then(r => setFetchedData(r.data))
        .catch(() => setFetchedData([]));
    }, [villageName]);

    // useEffect(() => {
    //     const fetchRelationships = async () => {
    //         if (formData && formData.id) {
    //           //  console.log('formData', formData);
    //             try {
    //                 const response = await axios.get(`${API_BASE_URL}/relationships/${formData.id}`);
    //                 const familyMembers = response.data.map((rel) => ({
    //                     relation: rel.relationship,
    //                     id: rel.relatedUser.id,
    //                     firstname: rel.relatedUser.first_name,
    //                     lastname: rel.relatedUser.last_name,
    //                     position: rel.relatedUser.position,
    //                     uid: rel.relatedUser.uid,
    //                     gender: rel.relatedUser.gender || '',
    //                 })).filter((member) => member.relation.toLowerCase() !== 'self');

    //                 setAdditionalFields([
    //                     {
    //                         relation: 'Self',
    //                         id: formData.id || '',
    //                         firstname: formData.first_name,
    //                         lastname: formData.last_name,
    //                         position: formData.position || '',
    //                         uid: formData.uid,
    //                         gender: formData.gender || '',
    //                     },
    //                     ...familyMembers,
    //                 ]);
    //               //  console.log('additionalFields', additionalFields);
    //             } catch (error) {
    //               //  console.error('Error fetching relationships:', error);
    //             }
    //         }
    //     };

    //     fetchRelationships();
    // }, [formData]);

    
    
    // ✅ FETCH RELATIONSHIPS — runs only when the logged-in user's id changes
  const formDataId = formData?.id;
  const formDataRef = useRef(formData);
  const fetchedForId = useRef(null); // prevent re-fetch on every formData change
  useEffect(() => { formDataRef.current = formData; }, [formData]);

  useEffect(() => {
    if (!formDataId) return;
    // Only fetch once per user id — prevents re-fetch when formData fields change
    if (fetchedForId.current === formDataId) return;
    fetchedForId.current = formDataId;

    const fd = formDataRef.current;

    // Set self row immediately
    setSelfRow({
        relation: 'Self',
        id: fd.id || '',
        firstname: fd.first_name || '',
        lastname: fd.last_name || '',
        position: fd.position || '',
        uid: fd.uid || '',
        gender: fd.gender || '',
    });

    // Reset new members only on first load for this user
    setNewMembers([]);

    // Fetch existing family members from API — shown but NOT re-sent on save
    axios.get(`${API_BASE_URL}/relationships/${formDataId}`)
        .then(r => {
            if (!r.data || !Array.isArray(r.data)) return;
            const fetched = r.data
                .map(rel => ({
                    relation: rel?.relationship || '',
                    // relationship_id is the correct field to use for DELETE /relationships/:id
                    id: rel?.relationship_id || rel?.relatedUser?.id || '',
                    firstname: rel?.relatedUser?.first_name || '',
                    lastname: rel?.relatedUser?.last_name || '',
                    position: rel?.relatedUser?.position || '',
                    uid: rel?.relatedUser?.uid || '',
                    gender: rel?.relatedUser?.gender || '',
                    _isExisting: true,
                }))
                .filter(m => m.relation && m.relation.toLowerCase() !== 'self');
            setExistingMembers(fetched);
        })
        .catch(e => console.error('Error fetching relationships:', e));
}, [formDataId]);
    
    // const generateUniqueId = useCallback(() => {
    //     // Always generate a guaranteed unique UID on frontend
    //     // Backend will also generate one as fallback if this is empty
    //     const random = Math.floor(100000 + Math.random() * 900000);
    //     const ts = Date.now().toString().slice(-6);
    //     return `91IND${random}${ts}`;
    // }, []);

const generateUniqueId = useCallback(() => {
      // UID format: 91IN + stateCode + districtCode(3) + villageLast3 + random(6)
      // stateCode/districtCode/villageCode come from /fetch-village-details (City table).
      const stateCode = String(fetchedData?.state_code || 'IN').trim().toUpperCase();
      const districtCode = String(fetchedData?.district_code || '000').replace(/\D/g, '').padStart(3, '0').slice(0, 3);
      const villageCode = String(fetchedData?.village_code || '').replace(/\D/g, '');
      const villageLast3 = (villageCode ? villageCode.slice(-3) : '000').padStart(3, '0');
      const random = String(Math.floor(100000 + Math.random() * 900000)); // 6 digits

      return `91IN${stateCode}${districtCode}${villageLast3}${random}`;
    }, [fetchedData?.state_code, fetchedData?.district_code, fetchedData?.village_code]);


    // const handleAddField = () => {
    //     if (fetchedData.length === 0) {
    //         alert("Please wait while village details are being fetched.");
    //         return;
    //     }
    //     //console.log('-----1----2----', fetchedData);
    //     const villageDetails = fetchedData;
    //     const newMember = {
    //         relation: '',
    //         id: '',
    //         firstname: '',
    //         lastname: '',
    //         position: '',
    //         tree_id: formData.tree_id,
    //         uid: generateUniqueId(villageDetails),
    //         gender: '',
    //         password: 'Admin@123'
    //     };
    //     //console.log('-----3----4----', newMember);
    //     setAdditionalFields((prevFields) => [...prevFields, newMember]);
    // };

    // ✅ ADD MEMBER BUTTON FIXED
   // ✅ ONLY CHANGES APPLIED — UI SAME

// Add Member: always adds exactly ONE new empty row
const handleAddField = () => {
    const newMember = {
        relation: '',
        id: '',
        firstname: '',
        lastname: '',
        position: '',
        tree_id: formData.tree_id,
        uid: '',
        gender: '',
        password: 'Admin@123',
    };
    setNewMembers(prev => [...prev, newMember]);
};
    
    // Delete: existing member → call DELETE API then remove from UI
    // New/empty member (no saved id) → remove from state only
    const handleRemoveField = async (memberOrIndex, isExisting) => {
        if (isExisting) {
            const member = memberOrIndex;
            console.log("Attempting to delete existing member ID:", member.id);
            
            // Always remove from UI immediately
            setExistingMembers(prev => prev.filter(m => m.id !== member.id));
            // Only call API if member has a valid DB id (not a temporary ID)
            if (member.id && !String(member.id).startsWith('temp')) {
                try {
                    // ✅ CHANGED: Call delete-user instead of relationships unlink
                    await axios.delete(`${API_BASE_URL}/delete-user/${member.id}`, { withCredentials: true });
                } catch (error) {
                    console.error('Error deleting relationship from DB:', error);
                }
            }
        } else {
            // New member not yet saved — remove from state only
            const index = Number(memberOrIndex);
            setNewMembers(prev => {
                const updated = prev.filter((_, i) => i !== index);
                const selfArr = selfRow ? [selfRow] : [];
                setAllFamilyMembers([...selfArr, ...existingMembers, ...updated]);
                return updated;
            });
        }
    };

    const handleInputChange = (e, newMemberIndex) => {
        const value = e.target.value;
        handleNewMemberChange(newMemberIndex, 'firstname', value);
        if (value.length >= 2) {
            setLoading(true);
            debouncedFetchUsers(value);
        } else {
            setSearchResults([]);
        }
        // openDropdownIndex tracks index within newMembers list
        setOpenDropdownIndex(newMemberIndex);
    };

    // Only updates newMembers fields (existing members are read-only in this form)
    const handleNewMemberChange = (newMemberIndex, field, value) => {
        setNewMembers(prev => {
            const updated = prev.map((m, i) =>
                i === newMemberIndex ? { ...m, [field]: value } : m
            );
            // Notify parent with self + existing + new for save payload
            const selfArr = selfRow ? [selfRow] : [];
            setAllFamilyMembers([...selfArr, ...existingMembers, ...updated]);
            return updated;
        });
        setValidationErrors(prev => {
            const e = { ...prev };
            delete e[`${field}-${newMemberIndex}`];
            return e;
        });
    };

    const handleSelectUser = (user, newMemberIndex) => {
        setNewMembers(prev => {
            const updated = prev.map((m, i) =>
                i === newMemberIndex
                    ? { ...m, firstname: user.first_name, lastname: user.last_name, id: user.id, uid: user.uid, gender: user.gender || '' }
                    : m
            );
            const selfArr = selfRow ? [selfRow] : [];
            setAllFamilyMembers([...selfArr, ...existingMembers, ...updated]);
            return updated;
        });
        setOpenDropdownIndex(null);
        setSearchResults([]);
    };

    useEffect(() => {
        setSearchResults([]);
        setOpenDropdownIndex(null);
    }, [editMode]);

    const downloadFamilyDetails = () => {
        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.setTextColor(40, 40, 40);
        doc.text('Family Members Details', 14, 20);
        doc.setFontSize(12);
        doc.text(`Primary Member: ${formData.first_name} ${formData.last_name}`, 14, 30);
        doc.text(`UID: ${formData.uid}`, 14, 37);
        doc.setFontSize(10);
        doc.text(`Default password: Admin@123`, 14, 43);

        const allRows = [...existingMembers, ...newMembers];
        const tableData = allRows.map(m => [
            m.relation, `${m.firstname} ${m.lastname}`, m.uid, m.gender, m.position
        ]);
        autoTable(doc, {
            startY: 45,
            head: [['Relation', 'Name', 'UID', 'Gender', 'Position']],
            body: tableData,
            headStyles: { fillColor: [124, 58, 237], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold' },
            bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
            alternateRowStyles: { fillColor: [245, 245, 245] },
            margin: { top: 45 },
            styles: { cellPadding: 3, fontSize: 9, valign: 'middle', overflow: 'linebreak', lineWidth: 0.1 },
            theme: 'grid'
        });
        doc.save(`family-members-${formData.uid}.pdf`);
    };

    // Validate only newMembers (existing are already saved; selfRow position validated separately)
    const validateFields = () => {
        const errors = {};

        // Validate selfRow position
        if (selfRow && !selfRow.position) {
            errors['position-self'] = 'Position is required';
        }

        newMembers.forEach((field, i) => {
            if (!field.relation) errors[`relation-${i}`] = 'Relationship is required';
            if (!field.firstname) errors[`firstname-${i}`] = 'First name is required';
            if (!field.lastname) errors[`lastname-${i}`] = 'Last name is required';
            if (!field.gender) errors[`gender-${i}`] = 'Gender is required';
            if (!field.position) errors[`position-${i}`] = 'Position is required';
        });

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    useImperativeHandle(ref, () => ({
        validate: validateFields
    }));

//     return (
//         <Box>
//             <Stack spacing={3}>
//                 {additionalFields.map((field, idx) => (
//                     <MemberCard key={idx}>
//                         <Stack spacing={2}>
//                             <Stack direction="row" alignItems="center" spacing={1}>
//                             <InfoTypeIcon sx={{
//                         ...(editMode ? {
//                           color: (theme) => theme.palette.primary.main,
//                           opacity: 1
//                         } : {
//                           color: 'text.secondary',
//                           opacity: 0.6
//                         })
//                       }}>
//                                 <PeopleIcon
//                                 />
//                                 </InfoTypeIcon>
//                                 <Typography variant="h6" fontWeight="bold" sx={{
//                                     ...(editMode && {
//                                         background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
//                                         backgroundClip: 'text',
//                                         WebkitBackgroundClip: 'text',
//                                         color: 'transparent',
//                                     }),
//                                     ...(!editMode && { color: 'grey' }) // Regular text color when not in edit mode
//                                 }}>
//                                     {idx === 0 ? 'Primary Member' : `Family Member ${idx}`}
//                                 </Typography>
//                             </Stack>

//                             <Divider />

//                             <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
//                                 <StyledTextField
//                                     select
//                                     label="Relationship"
//                                     value={field.relation}
//                                     onChange={(e) => handleFieldChange(idx, 'relation', e.target.value)}
//                                     fullWidth
//                                     disabled={idx === 0 || !editMode}
//                                     error={!!validationErrors[`relation-${idx}`]}
//                                     helperText={validationErrors[`relation-${idx}`]}
//                                 >
//                                     <MenuItem value="">Select Relationship</MenuItem>
//                                     {[
//                                         { display: 'Self', value: 'Self' },
//                                         { display: 'Father/पिता', value: 'father' },
//                                         { display: 'Mother/माँ', value: 'mother' },
//                                         { display: 'Son/बेटा', value: 'son' },
//                                         { display: 'Daughter/बेटी', value: 'daughter' },
//                                         { display: 'Husband/पति', value: 'husband' },
//                                         { display: 'Wife/पत्नी', value: 'wife' },
//                                         { display: 'Brother/भाई', value: 'brother' },
//                                         { display: 'Sister/बहन', value: 'sister' },
//                                     ].map(({ display, value }) => (
//                                         <MenuItem key={value} value={value}>
//                                             {display}
//                                         </MenuItem>
//                                     ))}
//                                 </StyledTextField>

//                                 <Box sx={{ position: 'relative', width: '100%', overflow: 'visible' }}>
//                                     <StyledTextField
//                                         label="First Name"
//                                         value={field.firstname}
//                                         onChange={(e) => handleInputChange(e, idx)}
//                                         fullWidth
//                                         disabled={idx === 0 || !editMode}
//                                         error={!!validationErrors[`firstname-${idx}`]}
//                                         helperText={validationErrors[`firstname-${idx}`]}
//                                     />

//                                     {/* Search Results Dropdown */}
//                                     {openDropdownIndex === idx && searchResults.length > 0 && (
//                                         <SearchResultsPaper>
//                                             <ClickAwayListener onClickAway={() => setOpenDropdownIndex(null)}>
//                                                 <List>
//                                                     {loading ? (
//                                                         <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
//                                                             <CircularProgress size={20} />
//                                                         </Box>
//                                                     ) : (
//                                                         searchResults.map((user, i) => (
//                                                             <SearchResultItem
//                                                                 key={i}
//                                                                 button
//                                                                 onClick={() => handleSelectUser(user, idx)}
//                                                             >
//                                                                 <ListItemText
//                                                                     primary={`${user.first_name} ${user.last_name}`}
//                                                                     secondary={`UID: ${user.uid}`}
//                                                                     primaryTypographyProps={{
//                                                                         fontWeight: 'medium'
//                                                                     }}
//                                                                 />
//                                                             </SearchResultItem>
//                                                         ))
//                                                     )}
//                                                     {searchResults.length === 0 && !loading && (
//                                                         <ListItem>
//                                                             <ListItemText
//                                                                 primary="No matching users found"
//                                                                 sx={{ color: 'text.secondary' }}
//                                                             />
//                                                         </ListItem>
//                                                     )}
//                                                 </List>
//                                             </ClickAwayListener>
//                                         </SearchResultsPaper>
//                                     )}
//                                 </Box>

//                                 <StyledTextField
//                                     label="Last Name"
//                                     value={field.lastname}
//                                     onChange={(e) => handleFieldChange(idx, 'lastname', e.target.value)}
//                                     fullWidth
//                                     disabled={idx === 0 || !editMode}
//                                     error={!!validationErrors[`lastname-${idx}`]}
//                                     helperText={validationErrors[`lastname-${idx}`]}
//                                 />

//                                 <StyledTextField
//                                     select
//                                     label="Gender"
//                                     value={field.gender}
//                                     onChange={(e) => handleFieldChange(idx, 'gender', e.target.value)}
//                                     fullWidth
//                                     disabled={!editMode}
//                                     required
//                                     error={!!validationErrors[`gender-${idx}`]}
//                                     helperText={validationErrors[`gender-${idx}`]}
//                                 >
//                                     <MenuItem value="">Select</MenuItem>
//                                     <MenuItem value="Male">Male</MenuItem>
//                                     <MenuItem value="Female">Female</MenuItem>
//                                 </StyledTextField>

//                                 <StyledTextField
//                                     select
//                                     label="Position in Family"
//                                     value={field.position}
//                                     onChange={(e) => handleFieldChange(idx, 'position', e.target.value)}
//                                     fullWidth
//                                     disabled={!editMode}
//                                     required
//                                     error={!!validationErrors[`position-${idx}`]}
//                                     helperText={validationErrors[`position-${idx}`]}
//                                 >
//                                     <MenuItem value="">Select</MenuItem>
//                                     {[...Array(14).keys()].map((num) => {
//                                         const position = (num + 1).toString();
//                                         return (
//                                             <MenuItem key={position} value={position}>
//                                                 {position}
//                                             </MenuItem>
//                                         );
//                                     })}
//                                 </StyledTextField>

//                                 {idx !== 0 && (
//                                     <DeleteButton
//                                         onClick={() => handleRemoveField(idx, field.id)}
//                                         disabled={!editMode}
//                                     >
//                                         <DeleteIcon />
//                                     </DeleteButton>
//                                 )}
//                             </Stack>
//                         </Stack>
//                     </MemberCard>
//                 ))}

//                 <Box sx={{
//                     display: 'flex',
//                     gap: 2,
//                     alignItems: 'center',
//                     justifyContent: 'flex-end',
//                     mt: 2
//                 }}>
//                     {additionalFields.length > 0 && editMode &&(
//                         <OutlinedActionButton
//                             onClick={downloadFamilyDetails}
//                             startIcon={<FileDownloadIcon />}
//                             disabled={!editMode}
//                         >
//                             Download Family Details
//                         </OutlinedActionButton>
//                     )}
//   {editMode && (
//                     <ActionButton
//                         onClick={handleAddField}
//                         disabled={!editMode}
//                         startIcon={<AddIcon />}
//                     >
//                         Add Family Member
//                     </ActionButton>
//   )}
//                 </Box>
//             </Stack>
//         </Box>
//     );

return (
    <Box>
        <Stack spacing={3}>

            {/* Self (Primary Member) row — always shown, index 0 */}
            {selfRow && (
                <MemberCard key="self">
                    <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <InfoTypeIcon><PeopleIcon /></InfoTypeIcon>
                            <Typography variant="h6" fontWeight="bold">Primary Member</Typography>
                        </Stack>
                        <Divider />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr auto' }, gap: 2, alignItems: 'center' }}>
                            <StyledTextField select label="Relationship" value={selfRow.relation} fullWidth disabled size="small">
                                <MenuItem value="Self">Self</MenuItem>
                            </StyledTextField>
                            <StyledTextField label="First Name" value={selfRow.firstname} fullWidth disabled size="small" />
                            <StyledTextField label="Last Name" value={selfRow.lastname} fullWidth disabled size="small" />
                            {/* <StyledTextField select label="Gender" value={selfRow.gender} fullWidth disabled size="small">
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </StyledTextField> */}
                            <StyledTextField
    select
    label="Gender"
    value={selfRow.gender || ''}
    onChange={(e) =>
        {
            const val = e.target.value;
            setSelfRow(prev => {
                const updated = { ...prev, gender: val };
                setAllFamilyMembers([updated, ...existingMembers, ...newMembers]);
                return updated;
            });
        }
    }
    fullWidth
    disabled={!editMode}
    size="small"
    error={!!validationErrors['gender-self']}
    helperText={validationErrors['gender-self']}
>
    <MenuItem value="">Select</MenuItem>
    <MenuItem value="Male">Male</MenuItem>
    <MenuItem value="Female">Female</MenuItem>
</StyledTextField>
                            <StyledTextField
                                select label="Position" value={selfRow.position || ''}
                                onChange={e => {
                                    const val = e.target.value;
                                    setSelfRow(prev => {
                                        const updated = { ...prev, position: val };
                                        setAllFamilyMembers([updated, ...existingMembers, ...newMembers]);
                                        return updated;
                                    });
                                }}
                                fullWidth disabled={!editMode} size="small"
                                error={!!validationErrors['position-self']}
                                helperText={validationErrors['position-self']}
                            >
                                <MenuItem value="">Select</MenuItem>
                                {[...Array(14).keys()].map(n => <MenuItem key={n} value={n + 1}>{n + 1}</MenuItem>)}
                            </StyledTextField>
                            <Box /> {/* spacer for delete column */}
                        </Box>
                    </Stack>
                </MemberCard>
            )}

            {/* Existing members — fetched from API, read-only, delete calls API */}
            {existingMembers.map((field) => (
                <MemberCard key={`existing-${field.id}`}>
                    <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <InfoTypeIcon><PeopleIcon /></InfoTypeIcon>
                            <Typography variant="h6" fontWeight="bold">Family Member</Typography>
                        </Stack>
                        <Divider />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr auto' }, gap: 2, alignItems: 'center' }}>
                            <StyledTextField select label="Relationship" value={field.relation} fullWidth disabled size="small">
                                <MenuItem value={field.relation}>{field.relation}</MenuItem>
                            </StyledTextField>
                            <StyledTextField label="First Name" value={field.firstname} fullWidth disabled size="small" />
                            <StyledTextField label="Last Name" value={field.lastname} fullWidth disabled size="small" />
                            <StyledTextField select label="Gender" value={field.gender} fullWidth disabled size="small">
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </StyledTextField>
                            <StyledTextField select label="Position" value={field.position || ''} fullWidth disabled size="small">
                                <MenuItem value="">Select</MenuItem>
                                {[...Array(14).keys()].map(n => <MenuItem key={n} value={n + 1}>{n + 1}</MenuItem>)}
                            </StyledTextField>
                            <Box sx={{ textAlign: 'center' }}>
                                <DeleteButton
                                    onClick={() => handleRemoveField(field, true)}
                                    disabled={!editMode}
                                >
                                    <DeleteIcon />
                                </DeleteButton>
                            </Box>
                        </Box>
                    </Stack>
                </MemberCard>
            ))}

            {/* New members — added via button, editable, delete removes from state only */}
            {newMembers.map((field, idx) => (
                <MemberCard key={field.uid || idx}>
                    <Stack spacing={3}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <InfoTypeIcon><PeopleIcon /></InfoTypeIcon>
                            <Typography variant="h6" fontWeight="bold">New Member {idx + 1}</Typography>
                        </Stack>
                        <Divider />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr 1fr auto' }, gap: 2, alignItems: 'center' }}>
                            <StyledTextField
                                select label="Relationship" value={field.relation}
                                onChange={e => handleNewMemberChange(idx, 'relation', e.target.value)}
                                fullWidth size="small"
                                error={!!validationErrors[`relation-${idx}`]}
                                helperText={validationErrors[`relation-${idx}`]}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="father">Father</MenuItem>
                                <MenuItem value="mother">Mother</MenuItem>
                                <MenuItem value="son">Son</MenuItem>
                                <MenuItem value="daughter">Daughter</MenuItem>
                                <MenuItem value="husband">Husband</MenuItem>
                                <MenuItem value="wife">Wife</MenuItem>
                                <MenuItem value="brother">Brother</MenuItem>
                                <MenuItem value="sister">Sister</MenuItem>
                                <MenuItem value="uncle">Uncle</MenuItem>
                                <MenuItem value="aunt">Aunt</MenuItem>
                                <MenuItem value="nephew">Nephew</MenuItem>
                                <MenuItem value="niece">Niece</MenuItem>
                                <MenuItem value="cousin">Cousin</MenuItem>
                                <MenuItem value="father-in-law">Father-in-law</MenuItem>
                                <MenuItem value="mother-in-law">Mother-in-law</MenuItem>
                                <MenuItem value="son-in-law">Son-in-law</MenuItem>
                                <MenuItem value="daughter-in-law">Daughter-in-law</MenuItem>
                                <MenuItem value="brother-in-law">Brother-in-law</MenuItem>
                                <MenuItem value="sister-in-law">Sister-in-law</MenuItem>
                                <MenuItem value="grandfather">Grandfather</MenuItem>
                                <MenuItem value="grandmother">Grandmother</MenuItem>
                            </StyledTextField>

                            <Box sx={{ position: 'relative' }}>
                                <StyledTextField
                                    label="First Name" value={field.firstname}
                                    onChange={e => handleInputChange(e, idx)}
                                    fullWidth size="small"
                                    error={!!validationErrors[`firstname-${idx}`]}
                                    helperText={validationErrors[`firstname-${idx}`]}
                                />
                                {openDropdownIndex === idx && (
                                    <SearchResultsPaper>
                                        <ClickAwayListener onClickAway={() => setOpenDropdownIndex(null)}>
                                            <Box>
                                                <List dense>
                                                    {loading ? (
                                                        <Box sx={{ p: 2, textAlign: 'center' }}><CircularProgress size={20} /></Box>
                                                    ) : searchResults.length > 0 ? (
                                                        searchResults.map((user, i) => (
                                                            <SearchResultItem key={user.id || i} onClick={() => handleSelectUser(user, idx)}>
                                                                <ListItemText primary={`${user.first_name} ${user.last_name}`} secondary={`UID: ${user.uid}`} />
                                                            </SearchResultItem>
                                                        ))
                                                    ) : (
                                                        <ListItem><ListItemText primary="No users found" /></ListItem>
                                                    )}
                                                </List>
                                            </Box>
                                        </ClickAwayListener>
                                    </SearchResultsPaper>
                                )}
                            </Box>

                            <StyledTextField
                                label="Last Name" value={field.lastname}
                                onChange={e => handleNewMemberChange(idx, 'lastname', e.target.value)}
                                fullWidth size="small"
                                error={!!validationErrors[`lastname-${idx}`]}
                                helperText={validationErrors[`lastname-${idx}`]}
                            />

                            <StyledTextField
                                select label="Gender" value={field.gender}
                                onChange={e => handleNewMemberChange(idx, 'gender', e.target.value)}
                                fullWidth size="small"
                                error={!!validationErrors[`gender-${idx}`]}
                                helperText={validationErrors[`gender-${idx}`]}
                            >
                                <MenuItem value="">Select</MenuItem>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </StyledTextField>

                            <StyledTextField
                                select label="Position" value={field.position}
                                onChange={e => handleNewMemberChange(idx, 'position', e.target.value)}
                                fullWidth size="small"
                                error={!!validationErrors[`position-${idx}`]}
                                helperText={validationErrors[`position-${idx}`]}
                            >
                                <MenuItem value="">Select</MenuItem>
                                {[...Array(14).keys()].map(n => <MenuItem key={n} value={n + 1}>{n + 1}</MenuItem>)}
                            </StyledTextField>

                            <Box sx={{ textAlign: 'center' }}>
                                <DeleteButton onClick={() => handleRemoveField(idx, false)} disabled={!editMode}>
                                    <DeleteIcon />
                                </DeleteButton>
                            </Box>
                        </Box>
                    </Stack>
                </MemberCard>
            ))}

            {/* ACTION BUTTONS */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-end' }}>
                {(existingMembers.length > 0 || newMembers.length > 0) && editMode && (
                    <OutlinedActionButton onClick={downloadFamilyDetails} startIcon={<FileDownloadIcon />}>
                        Download
                    </OutlinedActionButton>
                )}
                {editMode && (
                    <ActionButton onClick={handleAddField} startIcon={<AddIcon />}>
                        Add Member
                    </ActionButton>
                )}
            </Box>
        </Stack>
    </Box>
);


});

export default AddFamilyMember;
